import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Save, Share2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { hasAdminAccess } from "@/lib/admin-access";
import { AdminShell } from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { getSocialMeta, saveSocialMeta } from "@/lib/social-meta.functions";
import { SOCIAL_META_DEFAULTS, SOCIAL_META_IMAGE } from "@/lib/social-meta";

export const Route = createFileRoute("/admin/social-preview")({
  head: () => ({
    meta: [
      { title: "Social Preview - WeLivdIt Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminSocialPreviewPage,
});

function AdminSocialPreviewPage() {
  const navigate = useNavigate();
  const load = useServerFn(getSocialMeta);
  const save = useServerFn(saveSocialMeta);

  const [gate, setGate] = useState<"loading" | "ok" | "denied">("loading");
  const [title, setTitle] = useState(SOCIAL_META_DEFAULTS.title);
  const [description, setDescription] = useState(SOCIAL_META_DEFAULTS.description);
  const [saving, setSaving] = useState(false);

  // Live overlay stat pulled from the text being edited (e.g. "1,500+").
  const statOverlay = (`${title} ${description}`.match(/[\d][\d,]*\+/) ?? [])[0] ?? null;


  useEffect(() => {
    (async () => {
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        navigate({ to: "/auth", search: { redirect: "/admin/social-preview" } });
        return;
      }
      const ok = await hasAdminAccess(sess.session);
      setGate(ok ? "ok" : "denied");
      if (ok) {
        const meta = await load();
        setTitle(meta.title);
        setDescription(meta.description);
      }
    })();
  }, [navigate, load]);

  async function handleSave() {
    setSaving(true);
    try {
      await save({ data: { title: title.trim(), description: description.trim() } });
      // Reflect the change in the live document head immediately.
      const set = (sel: string, value: string) => {
        const el = document.head.querySelector<HTMLMetaElement>(sel);
        if (el) el.setAttribute("content", value);
      };
      set('meta[property="og:title"]', title.trim());
      set('meta[name="twitter:title"]', title.trim());
      set('meta[property="og:description"]', description.trim());
      set('meta[name="twitter:description"]', description.trim());
      toast.success("Social preview updated — new shares will show the updated text");

    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not save");
    } finally {
      setSaving(false);
    }
  }

  if (gate === "loading")
    return (
      <div className="grid min-h-[60vh] place-items-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  if (gate === "denied")
    return (
      <div className="grid min-h-[60vh] place-items-center px-5">
        <p className="text-muted-foreground">Not authorized.</p>
      </div>
    );

  return (
    <AdminShell
      title="Social Preview"
      description="Controls the title and description WhatsApp, LinkedIn and X show when someone shares a welivdit.com link. The preview image stays fixed."
    >
      <div className="mt-4 grid gap-6 lg:grid-cols-[1.1fr_1fr]">
        <section className="rounded-2xl border border-border bg-card p-6">
          <div className="flex items-center gap-2">
            <Share2 className="h-4 w-4 text-primary" />
            <h2 className="font-display text-lg font-semibold">Open Graph &amp; Twitter Card text</h2>
          </div>

          <div className="mt-5 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="og-title">Meta title (og:title / twitter:title)</Label>
              <Input id="og-title" maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} />
              <p className="text-xs text-muted-foreground">{title.length}/120 characters</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="og-description">Meta description (og:description / twitter:description)</Label>
              <Textarea
                id="og-description"
                rows={4}
                maxLength={300}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">{description.length}/300 characters</p>
            </div>
          </div>

          <div className="mt-5 flex items-center gap-2">
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Publish preview text
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setTitle(SOCIAL_META_DEFAULTS.title);
                setDescription(SOCIAL_META_DEFAULTS.description);
              }}
            >
              Reset to default
            </Button>
          </div>
        </section>

        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-display text-lg font-semibold">Live card preview</h2>
          <div className="mt-4 overflow-hidden rounded-xl border border-border">
            <div className="relative">
              <img src={SOCIAL_META_IMAGE} alt="Current social share image" className="w-full" loading="lazy" />
              {statOverlay && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/40 to-transparent p-4 text-center">
                  <p className="font-display text-2xl font-black leading-none text-white drop-shadow-sm sm:text-3xl">
                    {statOverlay}
                  </p>
                  <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/80">
                    US Visa Approvals
                  </p>
                </div>
              )}
            </div>

            <div className="bg-muted/40 p-4">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">welivdit.com</p>
              <p className="mt-1 line-clamp-2 text-sm font-semibold text-foreground">{title}</p>
              <p className="mt-1 line-clamp-3 text-xs text-muted-foreground">{description}</p>
            </div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Platforms cache previews. New shares pick up the change immediately; already-shared links refresh once the
            platform re-scrapes (you can force it in their link preview debugger).
          </p>
        </section>
      </div>
    </AdminShell>
  );
}
